import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, Pencil, Check, ListChecks, BookOpen, X } from "lucide-react";
import { toast } from "sonner";

const categories = ["Aptitude", "Reasoning", "Technical", "Verbal", "Versant", "AML & CTF", "Financial Crime", "Interview Prep", "Medical Billing", "AR Calling", "SAP MM"];
const difficulties = ["easy", "medium", "hard"];

export default function ContentEditor() {
  const [tab, setTab] = useState("quizzes");
  return (
    <div className="space-y-6" data-testid="content-editor-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Admin Tools</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-3">
          <Pencil className="w-7 h-7 text-primary" /> Content Editor
        </h1>
        <p className="text-muted-foreground mt-1">Hand-craft and tweak quizzes and courses alongside the AI generator.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 max-w-sm">
          <TabsTrigger value="quizzes" data-testid="editor-tab-quizzes"><ListChecks className="w-4 h-4 mr-2" /> Quizzes</TabsTrigger>
          <TabsTrigger value="courses" data-testid="editor-tab-courses"><BookOpen className="w-4 h-4 mr-2" /> Courses</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === "quizzes" ? <QuizManager /> : <CourseManager />}
    </div>
  );
}

const emptyQ = () => ({ question: "", options: ["", "", "", ""], correct: 0, explanation: "" });

function QuizManager() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["quizzes"], queryFn: async () => (await api.get("/quizzes")).data });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEditing("new"); setForm({ title: "", category: "Aptitude", difficulty: "medium", questions: [emptyQ()] }); };
  const openEdit = async (id) => {
    const { data: d } = await api.get(`/admin/quizzes/${id}`);
    const q = d.quiz;
    setEditing(id);
    setForm({
      title: q.title, category: q.category, difficulty: q.difficulty,
      questions: q.questions.map((x) => ({ question: x.question, options: x.options, correct: x.correct ?? 0, explanation: x.explanation ?? "" })),
    });
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Enter a title."); return; }
    for (const q of form.questions) {
      if (!q.question.trim() || q.options.some((o) => !o.trim())) { toast.error("Fill all questions and 4 options."); return; }
    }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/quizzes", form);
      else await api.put(`/admin/quizzes/${editing}`, form);
      qc.invalidateQueries({ queryKey: ["quizzes"] });
      toast.success("Quiz saved!");
      setEditing(null); setForm(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quiz?")) return;
    try { await api.delete(`/admin/quizzes/${id}`); qc.invalidateQueries({ queryKey: ["quizzes"] }); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const updateQ = (i, patch) => setForm({ ...form, questions: form.questions.map((q, idx) => idx === i ? { ...q, ...patch } : q) });
  const updateOpt = (i, oi, val) => updateQ(i, { options: form.questions[i].options.map((o, idx) => idx === oi ? val : o) });

  const quizzes = data?.quizzes || [];

  return (
    <div className="space-y-4">
      <Button onClick={openNew} className="rounded-md gap-2" data-testid="new-quiz-button"><Plus className="w-4 h-4" /> New Quiz</Button>
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {quizzes.map((q) => (
            <div key={q.id} className="p-4 rounded-md border border-border bg-card flex items-center justify-between" data-testid={`editor-quiz-${q.id}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{q.title}</p>
                <p className="text-xs text-muted-foreground font-mono">{q.category} · {q.num_questions} q {q.ai_generated && "· AI"}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => openEdit(q.id)} data-testid={`edit-quiz-${q.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(q.id)} data-testid={`delete-quiz-${q.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setForm(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "New Quiz" : "Edit Quiz"}</DialogTitle>
          <DialogDescription>Fill in the question, four options, and mark the correct answer.</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 rounded-md" data-testid="quiz-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5 rounded-md" data-testid="quiz-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Difficulty</Label>
                  <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                    <SelectTrigger className="mt-1.5 rounded-md" data-testid="quiz-difficulty-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{difficulties.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {form.questions.map((q, i) => (
                <div key={i} className="p-4 rounded-md border border-border space-y-3" data-testid={`quiz-question-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Question {i + 1}</span>
                    {form.questions.length > 1 && (
                      <button onClick={() => setForm({ ...form, questions: form.questions.filter((_, idx) => idx !== i) })} data-testid={`remove-question-${i}`}>
                        <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                      </button>
                    )}
                  </div>
                  <Textarea value={q.question} onChange={(e) => updateQ(i, { question: e.target.value })} placeholder="Question text" className="rounded-md" data-testid={`question-text-${i}`} />
                  <div className="space-y-2">
                    {q.options.map((o, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <button
                          onClick={() => updateQ(i, { correct: oi })}
                          data-testid={`correct-toggle-${i}-${oi}`}
                          className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 text-xs font-mono ${q.correct === oi ? "bg-success text-white" : "bg-secondary"}`}
                          title="Mark correct"
                        >
                          {q.correct === oi ? <Check className="w-3.5 h-3.5" /> : String.fromCharCode(65 + oi)}
                        </button>
                        <Input value={o} onChange={(e) => updateOpt(i, oi, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + oi)}`} className="rounded-md" data-testid={`option-input-${i}-${oi}`} />
                      </div>
                    ))}
                  </div>
                  <Input value={q.explanation} onChange={(e) => updateQ(i, { explanation: e.target.value })} placeholder="Explanation (optional)" className="rounded-md text-sm" data-testid={`explanation-input-${i}`} />
                </div>
              ))}

              <Button variant="outline" className="rounded-md gap-2 w-full" onClick={() => setForm({ ...form, questions: [...form.questions, emptyQ()] })} data-testid="add-question-button">
                <Plus className="w-4 h-4" /> Add Question
              </Button>
              <Button className="w-full rounded-md" onClick={save} disabled={saving} data-testid="save-quiz-button">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Quiz"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CourseManager() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["courses"], queryFn: async () => (await api.get("/courses")).data });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEditing("new"); setForm({ title: "", category: "Aptitude", difficulty: "medium", description: "", content: "" }); };
  const openEdit = (c) => { setEditing(c.id); setForm({ title: c.title, category: c.category, difficulty: c.difficulty, description: c.description, content: c.content }); };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content required."); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/courses", form);
      else await api.put(`/admin/courses/${editing}`, form);
      qc.invalidateQueries({ queryKey: ["courses"] });
      toast.success("Course saved!");
      setEditing(null); setForm(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    try { await api.delete(`/admin/courses/${id}`); qc.invalidateQueries({ queryKey: ["courses"] }); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const courses = data?.courses || [];

  return (
    <div className="space-y-4">
      <Button onClick={openNew} className="rounded-md gap-2" data-testid="new-course-button"><Plus className="w-4 h-4" /> New Course</Button>
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {courses.map((c) => (
            <div key={c.id} className="p-4 rounded-md border border-border bg-card flex items-center justify-between" data-testid={`editor-course-${c.id}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{c.title}</p>
                <p className="text-xs text-muted-foreground font-mono">{c.category} {c.ai_generated && "· AI"}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-course-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(c.id)} data-testid={`delete-course-${c.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setForm(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "New Course" : "Edit Course"}</DialogTitle>
          <DialogDescription>Add course details and markdown content for the lesson.</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 rounded-md" data-testid="course-title-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5 rounded-md" data-testid="course-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Difficulty</Label>
                  <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                    <SelectTrigger className="mt-1.5 rounded-md" data-testid="course-difficulty-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{difficulties.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 rounded-md" data-testid="course-desc-input" /></div>
              <div><Label>Content (Markdown)</Label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-1.5 rounded-md font-mono text-sm min-h-[200px]" data-testid="course-content-input" /></div>
              <Button className="w-full rounded-md" onClick={save} disabled={saving} data-testid="save-course-button">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Course"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
